import { useEffect, useState, type ReactNode } from "react";
import { Sidebar, useSidebarPin } from "./Sidebar";
import { Topbar } from "./Topbar";
import { CommandPalette } from "./CommandPalette";
import { useIsDesktop } from "@/lib/utils/useMediaQuery";
import { cn } from "@/lib/utils/cn";

/**
 * Layout principal de Nexus.
 *
 * Comportamiento responsivo:
 * - Desktop (≥1024px): sidebar fija colapsada (64px) o pineada (240px).
 *   Hover expande temporalmente sin pinear.
 * - Tablet (768-1023px): sidebar oculta, drawer desde botón ☰.
 * - Mobile (<768px): idem tablet pero con padding más compacto.
 *
 * El contenido principal nunca excede 1440px y queda centrado, con padding
 * fluido. Esto se siente más cercano a "app de escritorio" que a "página".
 */
export function AppShell({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const [pinned, togglePinned] = useSidebarPin();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl+K abre command palette
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Ancho efectivo del sidebar (solo aplica a desktop)
  const sidebarWidth = isDesktop ? (pinned ? 240 : 64) : 0;

  return (
    <div className="min-h-screen bg-bg-base">
      <Sidebar
        variant={isDesktop ? "desktop" : "drawer"}
        collapsed={!pinned}
        drawerOpen={drawerOpen}
        onTogglePinned={togglePinned}
        onCloseDrawer={() => setDrawerOpen(false)}
      />

      <div
        style={{ marginLeft: sidebarWidth }}
        className="transition-[margin-left] duration-220 ease-nx-out"
      >
        <Topbar
          showMenuButton={!isDesktop}
          onOpenDrawer={() => setDrawerOpen(true)}
          onOpenSearch={() => setPaletteOpen(true)}
        />

        <main
          className={cn(
            "px-4 sm:px-6 lg:px-8 py-5 lg:py-8",
            "max-w-[1440px] mx-auto",
          )}
        >
          {children}
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
